import * as signalR from "@microsoft/signalr";

/**
 * Chat SignalR Connection Manager
 * Uses HttpOnly cookies for authentication (same as notification hub)
 * The browser automatically attaches X-Access-Token cookie via withCredentials
 */

const HUB_URL = process.env.NEXT_PUBLIC_SIGNALR_URL_CHAT;
if (!HUB_URL) {
  throw new Error("NEXT_PUBLIC_SIGNALR_URL_CHAT is not configured");
}

/**
 * Creates a new SignalR connection for chat hub
 * Uses HttpOnly cookies for authentication (withCredentials: true)
 */
export const createChatConnection = (): signalR.HubConnection => {
  const connection = new signalR.HubConnectionBuilder()
    .withUrl(HUB_URL, {
      // For HttpOnly cookies, this must be true
      // Browser will automatically attach X-Access-Token cookie
      withCredentials: true,
    })
    .withAutomaticReconnect({
      nextRetryDelayInMilliseconds: (retryContext) => {
        // Exponential backoff: 0s, 2s, 10s, 30s, then 30s intervals
        if (retryContext.previousRetryCount === 0) return 0;
        if (retryContext.previousRetryCount === 1) return 2000;
        if (retryContext.previousRetryCount === 2) return 10000;
        return 30000;
      },
    })
    .configureLogging(signalR.LogLevel.Information)
    .build();

  return connection;
};

// Singleton instance to prevent multiple connections
let chatConnectionInstance: signalR.HubConnection | null = null;

/**
 * Gets or creates the singleton chat connection instance
 * @returns The SignalR hub connection for chat
 */
export const getChatConnection = (): signalR.HubConnection => {
  chatConnectionInstance ??= createChatConnection();
  return chatConnectionInstance;
};

/**
 * Resets the singleton connection instance
 * Useful for testing or when connection needs to be recreated
 */
export const resetChatConnection = (): void => {
  if (chatConnectionInstance) {
    chatConnectionInstance.stop().catch((err) => {
      console.error("Error stopping chat connection:", err);
    });
    chatConnectionInstance = null;
  }
};
